import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';

const NumberInput = ({
  onFocus = undefined,
  onBlur = undefined,
  onMouseDown = undefined,
  value = undefined,
  InputLabelProps = undefined,
  ...otherProps
}) => {
  const [focused, setFocused] = useState(false);

  // Selecting the existing text on focus means the very first keystroke
  // replaces it outright, instead of being inserted next to it (e.g. typing
  // "5" into a field showing "$0" would otherwise produce "$05"). This needs
  // TWO mechanisms together because of how the underlying react-number-format
  // (v4.9.4) library manages its own caret position - see the long comment
  // above wrappedOnMouseDown for the full explanation of why a single
  // `e.target.select()` in onFocus alone is not reliable.
  //
  // 1. wrappedOnMouseDown (below): deterministically selects on the click
  //    that FOCUSES the field - the common case - with no timing dependency.
  // 2. wrappedOnFocus (here): a deferred fallback for focus paths where no
  //    mousedown occurs at all (Tab navigation, programmatic .focus() calls).
  const wrappedOnFocus = (e) => {
    setFocused(true);
    const input = e.target;
    // Deferred (not synchronous): react-number-format's own onFocus handler
    // calls US from *inside* a setTimeout(fn, 0) it schedules itself, and
    // that same callback can ALSO schedule its OWN nested setTimeout(fn2, 0)
    // that re-applies a single caret position afterwards. Same-delay timers
    // run in scheduling order (FIFO), so queuing our select() in a brand new
    // timer here - scheduled only once we're already inside their callback -
    // reliably runs after their nested one, and wins.
    setTimeout(() => {
      input.select();
    }, 0);
    if (onFocus) onFocus(e);
  };
  const wrappedOnBlur = (e) => {
    setFocused(false);
    if (onBlur) onBlur(e);
  };
  // Deterministically select-all on the click that FOCUSES the field, rather
  // than relying only on timing (see wrappedOnFocus above). mousedown fires
  // before the browser's native "place caret at click position" behavior and
  // before react-number-format's own focus/mouseup caret-repositioning logic
  // ever runs, so preventing the default here and selecting manually means
  // there's no race to win at all for this case - it's simply already
  // decided by the time any of that logic looks at the selection.
  //
  // Only do this for the click that ACTUALLY focuses the field: once it's
  // already focused, clicking again should behave normally (place the caret
  // at the click position) so a user can still click into the middle of an
  // existing value to fix one digit.
  //
  // MUI's TextField does not forward onMouseDown to the underlying native
  // <input> the way it forwards onFocus/onBlur - it lands on the FormControl
  // root <div> instead, so this handler also receives mousedown events that
  // bubbled up from OTHER children inside that div (e.g. the shrunk label,
  // or helper text), where e.target has no .select()/.focus(). Guard on that
  // instead of assuming e.target is always the input.
  const wrappedOnMouseDown = (e) => {
    if (
      document.activeElement !== e.target &&
      typeof e.target.select === 'function'
    ) {
      e.preventDefault();
      e.target.focus();
      e.target.select();
    }
    if (onMouseDown) onMouseDown(e);
  };
  const shrink = (value !== null && value !== undefined) || focused;
  const inputLabelProps = {
    shrink,
    ...InputLabelProps,
  };
  // Deliberately NOT special-casing "focused && value === 0" to blank the
  // displayed value here: doing so means changing the controlled `value`
  // prop fed into the underlying react-number-format instance purely for
  // presentation. react-number-format treats any external value change as a
  // real update and re-fires onValueChange in response, which round-trips
  // back into the app's actual onChange handler - so a purely-visual "hide
  // the zero" trick was actually writing null into the real form state, then
  // immediately writing 0 back, producing a visible "flash" in anything
  // (like a footer summary) that reads the state directly. Selecting the
  // text on focus (above) already solves the practical problem (typing
  // replaces the existing value instead of appending to it) without needing
  // to touch the controlled value at all.
  //
  // `value` CAN legitimately be null/undefined - a "cleared" field, as
  // opposed to a value of 0 - and that needs to reach react-number-format as
  // an empty string, not as null/undefined. react-number-format's own source
  // (updateValueIfRequired) treats a null/undefined `value` prop as "no
  // controlled value provided" and falls back to redisplaying whatever it
  // last had internally, rather than blanking the field - so a genuinely
  // cleared value would otherwise silently reappear. This mapping is a
  // stable function of the real value only (not conditioned on `focused`),
  // so - unlike the trick described above - it can't oscillate independently
  // and round-trip through onChange.
  const safeValue = value === null || value === undefined ? '' : value;
  return (
    <TextField
      InputLabelProps={inputLabelProps}
      onFocus={wrappedOnFocus}
      onBlur={wrappedOnBlur}
      onMouseDown={wrappedOnMouseDown}
      value={safeValue}
      {...otherProps}
    />
  );
};

NumberInput.propTypes = {
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onMouseDown: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  InputLabelProps: PropTypes.object,
};

export default React.memo(NumberInput);
